import { useMemo, useState } from "react";
import { X } from "lucide-react";

export default function Gallery({ section, items }) {
  const label = section?.label || "Our world of weddings";
  const headline = section?.headline || "A few celebrations we've been lucky to be part of.";
  const subheadline = section?.subheadline || "";
  const categories = section?.categories || ["All"];
  const destinations = section?.destinations || [];

  const [active, setActive] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (active === "All") return items;
    return items.filter((i) => (i.category || "") === active);
  }, [active, items]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-ivory py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-8">
            <div className="reveal flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold" />
              <span className="eyebrow">{label}</span>
            </div>
            <h2
              data-testid="gallery-headline"
              className="reveal font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl tracking-tight text-charcoal"
            >
              {headline}
            </h2>
            {subheadline ? (
              <p className="reveal mt-5 max-w-xl text-charcoal/70 font-light leading-relaxed">
                {subheadline}
              </p>
            ) : null}
          </div>
        </div>

        {/* Destination tags */}
        {destinations.length ? (
          <div className="reveal mb-10 md:mb-14 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-beige py-4 md:py-5" data-testid="gallery-destinations">
            <span className="uppercase tracking-[0.3em] text-[10px] text-charcoal/55 mr-2">Destinations · </span>
            {destinations.map((d, i) => (
              <span key={d} className="font-display italic text-lg md:text-xl text-charcoal/85" data-testid={`gallery-destination-${i}`}>
                {d}
                {i < destinations.length - 1 ? <span className="text-gold mx-3">·</span> : null}
              </span>
            ))}
          </div>
        ) : null}

        {/* Category filter */}
        <div className="reveal flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-12">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`gallery-cat-${c.toLowerCase()}`}
              onClick={() => setActive(c)}
              className={`px-4 h-9 text-[10.5px] uppercase tracking-[0.22em] border transition-colors ${
                active === c
                  ? "bg-teal-600 text-ivory border-teal-600"
                  : "bg-transparent text-charcoal border-charcoal/20 hover:border-teal-600 hover:text-teal-600"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Masonry */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {filtered.map((item, i) => (
            <figure
              key={item.id || i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightboxIdx(i)}
              className="reveal mb-4 md:mb-6 break-inside-avoid img-zoom-wrap cursor-pointer group relative"
            >
              <img
                src={item.image_url}
                alt={item.wedding_name || item.title || "Wedding"}
                loading="lazy"
                className="w-full h-auto object-cover"
              />
              <figcaption className="absolute inset-0 bg-charcoal/55 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-5 text-ivory">
                <div className="font-display text-xl md:text-2xl">{item.wedding_name || item.title}</div>
                {item.location ? (
                  <div className="mt-1 uppercase tracking-[0.25em] text-[10px] text-ivory/85">
                    {item.location}
                  </div>
                ) : null}
              </figcaption>
            </figure>
          ))}
          {filtered.length === 0 ? (
            <div className="text-center text-charcoal/50 font-light py-12">No images in this category yet.</div>
          ) : null}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && filtered[lightboxIdx] ? (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightboxIdx(null)}
          className="fixed inset-0 z-[80] bg-charcoal/95 flex items-center justify-center p-4 md:p-10"
        >
          <button
            type="button"
            onClick={() => setLightboxIdx(null)}
            data-testid="gallery-lightbox-close"
            className="absolute top-5 right-5 h-11 w-11 flex items-center justify-center text-ivory border border-ivory/30 hover:bg-ivory hover:text-charcoal transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={filtered[lightboxIdx].image_url}
            alt={filtered[lightboxIdx].wedding_name || filtered[lightboxIdx].title}
            className="max-h-[80vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 inset-x-0 px-6 md:px-12 text-center text-ivory">
            <div className="font-display text-2xl md:text-3xl">{filtered[lightboxIdx].wedding_name || filtered[lightboxIdx].title}</div>
            {filtered[lightboxIdx].location ? (
              <div className="mt-1 uppercase tracking-[0.3em] text-[10px] text-ivory/70">
                {filtered[lightboxIdx].location}
                {filtered[lightboxIdx].category ? ` · ${filtered[lightboxIdx].category}` : ""}
              </div>
            ) : null}
            {filtered[lightboxIdx].description ? (
              <p className="mt-3 max-w-2xl mx-auto text-ivory/80 font-light text-sm md:text-base leading-relaxed">
                {filtered[lightboxIdx].description}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
